import { canMeasure } from "../measurement/consent";
import { useCallback, useRef } from "react";
import { getOrCreateTrackingUuid, reportFunnelAction, reportMilestone } from "../api/tracking";

type UseFunnelTrackingOptions = {
  totalSteps: number;
};

export const useFunnelTracking = ({ totalSteps }: UseFunnelTrackingOptions) => {
  const highestReportedSeqRef = useRef<number>(0);
  const uidRef = useRef<string | null>(null);
  const initialReportedRef = useRef(false);

  const getUid = (): string => {
    if (!uidRef.current) {
      uidRef.current = getOrCreateTrackingUuid();
    }
    return uidRef.current;
  };

  const reportNavigation = useCallback(
    (stepIndex: number) => {
      if (!canMeasure()) return;
      const seq = stepIndex + 1;
      if (seq <= highestReportedSeqRef.current) {
        return;
      }
      highestReportedSeqRef.current = seq;

      const uid = getUid();
      const isFirst = !initialReportedRef.current;
      initialReportedRef.current = true;

      reportFunnelAction({
        uid,
        at: "navigate",
        seq,
        total: totalSteps,
        ...(isFirst
          ? {
              lp: window.location.pathname,
              lq: window.location.search || undefined
            }
          : {})
      });
    },
    [totalSteps]
  );

  const reportButton = useCallback((value: string) => {
    if (value === "open_checkout") reportMilestone("checkout");
    const uid = getUid();
    reportFunnelAction({
      uid,
      at: "button",
      val: value
    });
  }, []);

  const reportLeadLink = useCallback((leadId: number) => {
    const uid = getUid();
    reportFunnelAction({
      uid,
      at: "lead_link",
      val: String(leadId),
      lid: leadId
    });
    reportMilestone("email_saved");
  }, []);

  return {
    reportNavigation,
    reportButton,
    reportLeadLink
  };
};
